import { mapGetters } from "vuex";

import UserAvatar from "@/components/UserAvatar";
import UploaderIdentityDocuments from "@/components/UploaderIdentityDocuments";
import UserPhoneControl from "@/components/UserPhoneControl";
import UserEmailControl from "@/components/UserEmailControl";
import UserPasswordControl from "@/components/UserPasswordControl";

import { required, minLength, maxLength } from "vuelidate/lib/validators";
import Multiselect from "vue-multiselect";

import CloseIcon from "@/assets/images/icons/close.svg?inline";

import {
  UPDATE_USER_PROFILE_INFO,
  SIGN_OUT,
  PLACES_SUGGESTIONS,
  CREATE_AGENT_LOCATION,
  DELETE_AGENT_LOCATION,
  REMOVE_ID_DOCUMENT,
  DELETE_USER
} from "../gql/AgentProfileQueries";

export default {
  name: "agent_profile_info",

  data() {
    return {
      submitProfileInfoLoading: false,
      fieldTypeOptions: [
        "commercial",
        "condominium",
        "farm",
        "land",
        "multifamily",
        "rental",
        "residential"
      ],
      specialtiesOptions: ["Condo", "Luxury Condos", "Homes", "Luxury Homes"],

      userInfo: {},

      submitSingOut: false,

      submitLicenseLoading: false,

      locationOptions: [],
      skiplocationsQuery: true,
      locationRequestQuery: null,
      isLocationsLoading: false,
      agentLoacalLocations: [],

      submitProfessionalInfoLoading: false
    };
  },

  validations: {
    userInfo: {
      firstName: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(64)
      },
      lastName: {
        minLength: minLength(2),
        maxLength: maxLength(64)
      },
      facebook: {
        minLength: minLength(3),
        maxLength: maxLength(64)
      },
      linkedin: {
        minLength: minLength(3),
        maxLength: maxLength(64)
      },
      aboutMe: {
        maxLength: maxLength(400)
      },
      brokerName: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(64)
      },
      licence: {
        required,
        minLength: minLength(6),
        maxLength: maxLength(64)
      },
      agentSince: {
        maxLength: maxLength(64)
      },
      otherLicenses: {
        maxLength: maxLength(64)
      },
      brokerageAddress: {
        maxLength: maxLength(64)
      }
    }
  },

  apollo: {
    places: {
      query: PLACES_SUGGESTIONS,
      variables() {
        return {
          address: this.locationRequestQuery
        };
      },

      skip() {
        return this.skiplocationsQuery;
      },

      result() {
        this.skiplocationsQuery = true;
        this.isLocationsLoading = false;
      }
    }
  },

  mounted: function() {
    if (this.currentUser.id) {
      this.userInfo = JSON.parse(JSON.stringify(this.currentUser));
    }
  },

  computed: {
    ...mapGetters({
      currentUser: "getUser"
    }),

    isUserLoaded() {
      return !!this.userInfo.id;
    },

    filteredLocationOptions() {
      const filtered = [];

      if (!this.places) {
        return [];
      }

      this.places.forEach(r => {
        filtered.push({
          type: r.type,
          places: r.places,
          limit: 8,
          onSelected: selected => {
            this.selected = selected.item;
          }
        });
      });

      return Object.freeze(filtered);
    }
  },

  watch: {
    currentUser: function(newValues) {
      this.userInfo = JSON.parse(JSON.stringify(newValues));
    }
  },

  methods: {
    selectedSpecialties() {
      this.$apollo
        .mutate({
          mutation: UPDATE_USER_PROFILE_INFO,
          fetchPolicy: "no-cache",
          variables: {
            specialties: this.userInfo.specialties
              ? this.userInfo.specialties
              : []
          }
        })
        .then(({ data: { updateProfile: { user } } }) => {
          this.$store.commit("setUserInfo", user);
        })
        .catch(mutationError => {
          const errors = mutationError.graphQLErrors;

          for (let error in errors) {
            this.$toasted.error(errors[error].message);
          }
        });
    },

    selectedHouseType() {
      this.$apollo
        .mutate({
          mutation: UPDATE_USER_PROFILE_INFO,
          fetchPolicy: "no-cache",
          variables: {
            propertyTypes: this.userInfo.propertyTypes
              ? this.userInfo.propertyTypes
              : []
          }
        })
        .then(({ data: { updateProfile: { user } } }) => {
          this.$store.commit("setUserInfo", user);
        })
        .catch(mutationError => {
          const errors = mutationError.graphQLErrors;

          for (let error in errors) {
            this.$toasted.error(errors[error].message);
          }
        });
    },

    selectedLocation(selectedOption) {
      this.$apollo
        .mutate({
          mutation: CREATE_AGENT_LOCATION,
          variables: {
            description: selectedOption.description,
            placeId: selectedOption.placeId
          }
        })
        .then(({ data }) => {
          this.currentUser.agentLocations =
            data.createAgentLocation.agentLocations;
        })
        .catch(mutationError => {
          const errors = mutationError.graphQLErrors;

          for (let error in errors) {
            this.$toasted.error(errors[error].message);
          }
        });
    },

    removedLocation(removedOption, id) {
      this.$apollo.mutate({
        mutation: DELETE_AGENT_LOCATION,
        variables: {
          id: removedOption.id
        }
      });
    },

    addHouseType(newTag) {
      const tag = {
        name: newTag,
        code: newTag.substring(0, 2) + Math.floor(Math.random() * 10000000)
      };
      this.options.push(tag);
      this.value.push(tag);
    },

    submitProfileInfo() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.submitProfileInfoLoading = true;

        this.$apollo
          .mutate({
            mutation: UPDATE_USER_PROFILE_INFO,
            fetchPolicy: "no-cache",
            variables: {
              firstName: this.userInfo.firstName,
              lastName: this.userInfo.lastName,
              facebook: this.userInfo.facebook,
              linkedin: this.userInfo.linkedin,
              aboutMe: this.userInfo.aboutMe
            }
          })
          .then(({ data: { updateProfile: { user } } }) => {
            this.$store.commit("setUserInfo", user);
          })
          .catch(mutationError => {
            const errors = mutationError.graphQLErrors;

            for (let error in errors) {
              this.$toasted.error(errors[error].message);
            }
          })
          .finally(() => {
            this.submitProfileInfoLoading = false;
          });
      }
    },

    submitProfessionalInfo() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.submitProfessionalInfoLoading = true;

        this.$apollo
          .mutate({
            mutation: UPDATE_USER_PROFILE_INFO,
            fetchPolicy: "no-cache",
            variables: {
              agentSince: this.userInfo.agentSince,
              otherLicenses: this.userInfo.otherLicenses
            }
          })
          .then(({ data: { updateProfile: { user } } }) => {
            this.$store.commit("setUserInfo", user);
          })
          .catch(mutationError => {
            const errors = mutationError.graphQLErrors;

            for (let error in errors) {
              this.$toasted.error(errors[error].message);
            }
          })
          .finally(() => {
            this.submitProfessionalInfoLoading = false;
          });
      }
    },

    asyncFindLocations(query) {
      if (query.length > 2) {
        this.locationRequestQuery = query;
        this.skiplocationsQuery = false;

        this.isLocationsLoading = true;
      }
    },

    submitIdentityForm() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.submitLicenseLoading = true;

        this.$apollo
          .mutate({
            mutation: UPDATE_USER_PROFILE_INFO,
            fetchPolicy: "no-cache",
            variables: {
              licence: this.userInfo.licence,
              brokerName: this.userInfo.brokerName,
              brokerageAddress: this.userInfo.brokerageAddress,
              businessPhone: this.userInfo.businessPhone,
              brokeragePhone: this.userInfo.brokeragePhone,
              managingBrokerName: this.userInfo.managingBrokerName,
              brokerLicenseNumberAndState: this.userInfo.brokerLicenseNumberAndState
            }
          })
          .then(({ data: { updateProfile: { user } } }) => {
            this.$store.commit("setUserInfo", user);
          })
          .catch(mutationError => {
            const errors = mutationError.graphQLErrors;

            for (let error in errors) {
              this.$toasted.error(errors[error].message);
            }
          })
          .finally(() => {
            this.submitLicenseLoading = false;
          });
      }
    },

    signOutRequest(isDeleteRequest) {
      let type = SIGN_OUT;

      if (isDeleteRequest) {
        type = DELETE_USER;
      }

      this.$apollo
        .mutate({
          mutation: type
        })
        .then(() => {
          this.$store.dispatch("destroyUser");

          this.$router.push({
            name: "home"
          });
        })
        .catch(mutationError => {
          const errors = mutationError.graphQLErrors;

          for (let error in errors) {
            this.$toasted.error(errors[error].message);
          }
        })
        .finally(() => {
          this.submitSingOut = false;
        });
    },

    signOut() {
      if (confirm("Do you really want to sign out?")) {
        this.submitSingOut = true;

        this.signOutRequest();
      }
    },

    hideModal() {
      this.$refs.modalDeleteAccount.hide();
    },

    removeAccount() {
      this.hideModal;
      this.signOutRequest(true);
    }
  },

  components: {
    UserAvatar,
    Multiselect,
    UploaderIdentityDocuments,
    UserPhoneControl,
    UserEmailControl,
    UserPasswordControl,
    CloseIcon
  }
};
